import { useMemo, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { useProjectTotals } from '../lib/team-data'
import { EmptyState } from '../components/ui'
import { InfoDot } from '../components/InfoDot'
import { clientPrefix, initials, pluralise } from '../lib/util'
import { binned, entryMatches, idsOfKind, liveOfKind, type Client } from '../lib/types'
import { formatMoney, workspaceCurrency } from '../lib/billing'

export function ClientsScreen() {
  const { ws, act, setModal, flash, confirm, canSeeAll } = useWorkspace()
  const { totals } = useProjectTotals(ws.id)
  const currency = workspaceCurrency(ws.rates)

  /*
   * Clients, and nothing else.
   *
   * Prospects live on their own screen and are counted, searched and listed
   * only there. Taken once at the top so every figure below is drawn from
   * the same set -- a count and a list that disagreed about what a client is
   * would be worse than either.
   */
  const clients = useMemo(() => liveOfKind(ws.clients, 'client'), [ws.clients])

  // Projects belonging to a prospect are not this screen's either, or the
  // "active projects" figure would describe a list it does not show.
  const prospectIds = useMemo(() => idsOfKind(ws.clients, 'prospect'), [ws.clients])
  const projects = useMemo(
    () => ws.projects.filter((p) => !(p.clientId && prospectIds.has(p.clientId))),
    [ws.projects, prospectIds],
  )

  const activeProjects = projects.filter((p) => p.status === 'active').length

  const [search, setSearch] = useState('')
  const [prefix, setPrefix] = useState('')

  /*
   * The groups come from the names that actually exist, so the row of buttons
   * follows the client list instead of being a fixed A-Z most of which would
   * match nothing. Counted, because a group showing how many it holds is the
   * difference between a filter and a guess.
   */
  const groups = useMemo(() => {
    const counts = new Map<string, number>()
    for (const c of clients) {
      const g = clientPrefix(c.name)
      counts.set(g, (counts.get(g) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [clients])

  // A group that no longer exists -- its last client renamed or deleted --
  // would otherwise leave the list stuck showing nothing, with the button that
  // caused it gone from the screen.
  const activePrefix = groups.some(([g]) => g === prefix) ? prefix : ''

  const shown = useMemo(
    () =>
      clients.filter((c) => {
        if (activePrefix && clientPrefix(c.name) !== activePrefix) return false
        // The same matcher the Timer and Timesheet searches use, so a query
        // behaves the same wherever it is typed.
        return entryMatches(search, { clientName: c.name })
      }),
    [clients, activePrefix, search],
  )

  /*
   * The bin comes out of the same snapshot as everything else, because
   * binned clients stay in it -- that is what keeps their logged time
   * visible. Every write returns a fresh snapshot, so there is nothing to
   * refetch and no second copy of this list to fall behind.
   */
  // Clients only, here as everywhere. A deleted prospect is restored and
  // purged from the Prospects screen's own bin, never from this one.
  const inBin = useMemo(() => binned(ws.clients).filter((c) => !c.isProspect), [ws.clients])
  const [binOpen, setBinOpen] = useState(false)

  /*
   * The end of the line, so it asks for the name to be typed.
   *
   * requireText is this codebase's gesture for an action a second click
   * cannot undo, and reaching for it is a statement that the thing really is
   * irreversible. This one is: the row goes, and its projects keep their
   * time but lose the client for good.
   */
  const purge = (c: Client) =>
    confirm({
      title: `Delete ${c.name} for good?`,
      body:
        'This cannot be undone and there is no way back from it. Any projects ' +
        'that belonged to this client keep their time but lose the client ' +
        'permanently. Type the name to confirm.',
      cta: 'Delete for good',
      danger: true,
      requireText: c.name,
      onConfirm: () => void act.purgeClient(c.id),
    })

  return (
    <section className="screen">
      {/* The totals below come from a view scoped by RLS, so a member sees
          their own hours and an admin sees the team's -- in an identical
          shape, with nothing to tell the two apart. Saying which one this is
          costs a line and stops the figure being read as the project's when
          it is only yours. */}
      {!canSeeAll && (
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 4px' }}>
          Hours shown are your own. Owners and managers see the whole team's.
        </p>
      )}
      <div className="row" style={{ gap: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--mut)' }}>
          {pluralise(clients.length, 'client')} · {pluralise(activeProjects, 'active project')}
        </div>
        <button type="button" className="btn btn--primary push" onClick={() => setModal({ kind: 'client' })}>
          + New client
        </button>
      </div>

      {clients.length > 0 && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="input"
            style={{ flex: '1 1 180px', maxWidth: 260, padding: '8px 11px' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients…"
            aria-label="Search clients by name"
          />
          <button
            type="button"
            className="chip"
            aria-pressed={activePrefix === ''}
            onClick={() => setPrefix('')}
          >
            All {clients.length}
          </button>
          {groups.map(([g, n]) => (
            <button
              key={g}
              type="button"
              className="chip"
              aria-pressed={activePrefix === g}
              onClick={() => setPrefix(activePrefix === g ? '' : g)}
            >
              {g} {n}
            </button>
          ))}
        </div>
      )}

      {!clients.length ? (
        <EmptyState
          title="No clients yet"
          body="Add a client to group projects together and see hours and billing rolled up per account."
          action={{ label: '+ New client', onClick: () => setModal({ kind: 'client' }) }}
        />
      ) : (
        <div className="card card--clip">
          <div
            className="client-grid eyebrow"
            style={{ padding: '11px 16px', background: 'var(--surf2)', borderBottom: '1px solid var(--line2)' }}
          >
            <div>Client</div>
            <div>Contact</div>
            <div style={{ textAlign: 'right' }}>Projects</div>
            <div style={{ textAlign: 'right' }}>Hours</div>
            <div style={{ textAlign: 'right' }}>Billed</div>
            <div style={{ width: 132 }} />
          </div>

          {/* A filter that matches nothing left a bare header row, which reads
              as a broken screen rather than an empty result. */}
          {shown.length === 0 && (
            <div style={{ padding: '18px 16px', fontSize: 13, color: 'var(--mut)' }}>
              No client matches{search.trim() ? ' "' + search.trim() + '"' : ''}
              {activePrefix ? ' in ' + activePrefix : ''}.{' '}
              <button
                type="button"
                className="linkbtn"
                onClick={() => {
                  setSearch('')
                  setPrefix('')
                }}
              >
                Clear the filter
              </button>
            </div>
          )}

          {shown.map((c) => {
            const mine = projects.filter((p) => p.clientId === c.id)
            const ids = new Set(mine.map((p) => p.id))
            // Summed from project totals rather than by walking every entry.
            const rolled = [...ids].reduce(
              (acc, id) => {
                const t = totals.get(id)
                acc.sec += t?.seconds ?? 0
                acc.billable += t?.billableSeconds ?? 0
                acc.count += t?.entryCount ?? 0
                return acc
              },
              { sec: 0, billable: 0, count: 0 },
            )
            const sec = rolled.sec
            // Priced by entry_billing and summed in SQL. This used to apply
            // each project's hourly rate to the aggregated billable seconds,
            // which skipped the imported, task-fixed, project-fixed and
            // user-hourly steps that take precedence -- so the figure here
            // could not agree with Reports or the export.
            const amount = [...ids].reduce((a, id) => a + (totals.get(id)?.amount ?? 0), 0)

            return (
              <div key={c.id} className="client-grid client-row">
                <div className="row" style={{ gap: 10, minWidth: 0 }}>
                  <span className="client-avatar" aria-hidden>
                    {initials(c.name)}
                  </span>
                  <span className="trunc" style={{ fontSize: 14, fontWeight: 500 }}>
                    {c.name}
                  </span>
                  {c.status === 'archived' && <span className="status status--off">Archived</span>}
                </div>
                <div className="trunc" style={{ fontSize: 13, color: 'var(--mut)' }}>
                  {c.contact}
                </div>
                <div className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                  {mine.length}
                </div>
                <div className="mono" style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 500 }}>
                  {(sec / 3600).toFixed(1)}h
                </div>
                <div className="mono" style={{ textAlign: 'right', fontSize: 13.5 }}>
                  {formatMoney(amount, currency)}
                </div>
                <div style={{ width: 132, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <InfoDot createdBy={c.createdBy} createdAt={c.createdAt} what="client" />
                  <>
                    <button
                      type="button"
                      className="ibtn"
                      title="Edit"
                      aria-label={`Edit ${c.name}`}
                      onClick={() => setModal({ kind: 'client', client: c })}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="ibtn"
                      title={c.status === 'active' ? 'Archive' : 'Restore'}
                      aria-label={`${c.status === 'active' ? 'Archive' : 'Restore'} ${c.name}`}
                      onClick={() =>
                        void act
                          .updateClient(c.id, { status: c.status === 'active' ? 'archived' : 'active' })
                          .then((ok) => {
                            if (ok) flash(c.status === 'active' ? `${c.name} archived` : `${c.name} restored`)
                          })
                      }
                    >
                      ⧉
                    </button>
                    <button
                      type="button"
                      className="ibtn ibtn--del"
                      title="Delete"
                      aria-label={`Delete ${c.name}`}
                      onClick={() =>
                        confirm({
                          title: `Delete ${c.name}?`,
                          body:
                            (mine.length
                              ? `${pluralise(mine.length, 'project')} will stop rolling up here, and time entries are untouched. `
                              : 'No projects are attached. ') +
                            'It goes to Deleted clients below, where you can put it back.',
                          cta: 'Delete client',
                          danger: true,
                          onConfirm: () => void act.binClient(c.id),
                        })
                      }
                    >
                      ✕
                    </button>
                  </>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ---------- the bin ----------
          Below the list and collapsed, because it is somewhere you go on
          purpose after a mistake, not something to read past every day. It
          is absent entirely for a member: restoring and purging are both
          admin actions, and an expander that only ever refuses is worse
          than no expander. */}
      {canSeeAll && inBin.length > 0 && (
        <div className="card card--clip">
          <button
            type="button"
            className="spread"
            aria-expanded={binOpen}
            onClick={() => setBinOpen((v) => !v)}
            style={{
              width: '100%',
              padding: '11px 16px',
              background: 'var(--surf2)',
              border: 0,
              cursor: 'pointer',
              gap: 8,
              textAlign: 'left',
            }}
          >
            <span
              aria-hidden
              style={{
                fontSize: 10,
                transform: binOpen ? 'none' : 'rotate(-90deg)',
                transition: 'transform .12s',
              }}
            >
              ▾
            </span>
            <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 13 }}>
              Deleted clients
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>
              {pluralise(inBin.length, 'client')}
            </span>
          </button>

          {binOpen && (
            <>
              <p
                className="pretty"
                style={{
                  margin: 0,
                  padding: '10px 16px 0',
                  fontSize: 12.5,
                  color: 'var(--mut)',
                }}
              >
                These are out of the lists and pickers, so no new work can be
                attached to them. Everything already logged against them stays
                exactly where it is — visible in the timer, the reports and the
                exports — until a client is deleted for good from here.
              </p>

              {inBin.map((c) => (
                <div
                  key={c.id}
                  className="spread"
                  style={{
                    padding: '11px 16px',
                    borderTop: '1px solid var(--line2)',
                    gap: 10,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="trunc" style={{ fontWeight: 500 }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                      Deleted {c.deletedAt ? new Date(c.deletedAt).toLocaleString() : ''}
                      {c.contact ? ` · ${c.contact}` : ''}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void act.restoreClient(c.id, c.name)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => purge(c)}
                    >
                      Delete for good
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </section>
  )
}
